import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import ProductService from "../../../../../../service/ProductService";
import { useToastContext } from "../../../../../../contexts/ToastContext/ToastContext";
import useUploadFiles from "../../../../../../hooks/useUploadFiles";
import useGetUserExtrabonus from "../../../../../../hooks/useGetUserExtrabonus";
import {
  CUSTOMER_MAX_PERCENT_CASHBACK,
  CUSTOMER_MIN_PERCENT_CASHBACK,
  MANAGER_MAX_PERCENT_CASHBACK,
  MANAGER_MIN_PERCENT_CASHBACK,
  PARTNER_MAX_PERCENT_CASHBACK,
  PARTNER_MIN_PERCENT_CASHBACK,
  SELLER_MAX_PERCENT_CASHBACK,
  SELLER_MIN_PERCENT_CASHBACK,
} from "../../../../../../utilities/constants";
import {
  MAX_IMAGES,
  mapFreightToShippingOption,
  mapShippingOptionToFreight,
} from "./constants";
import {
  buildVariationCombinations,
  cloudinaryPreview,
  createLocalId,
  discountToPromotionalPrice,
  getCategoriesList,
  getCategoryLabel,
  getSubcategoryIdForDownloadType,
  mergeCombinations,
  promotionalPriceToDiscount,
} from "./utils";

const getCashbackBoundsByPlan = (plan) => {
  switch (plan) {
    case "Customer":
      return { min: CUSTOMER_MIN_PERCENT_CASHBACK, max: CUSTOMER_MAX_PERCENT_CASHBACK };
    case "Partner":
      return { min: PARTNER_MIN_PERCENT_CASHBACK, max: PARTNER_MAX_PERCENT_CASHBACK };
    case "Manager":
      return { min: MANAGER_MIN_PERCENT_CASHBACK, max: MANAGER_MAX_PERCENT_CASHBACK };
    case "Seller":
      return { min: SELLER_MIN_PERCENT_CASHBACK, max: SELLER_MAX_PERCENT_CASHBACK };
    default:
      return { min: CUSTOMER_MIN_PERCENT_CASHBACK, max: CUSTOMER_MAX_PERCENT_CASHBACK };
  }
};

const getCashbackErrorCodeByPlan = (plan) => {
  if (plan === "Partner") return 500070;
  if (plan === "Manager") return 500071;
  if (plan === "Seller") return 500168;
  return 500195;
};

const canStorePublishProducts = (plan, userInfo) => {
  if (plan !== "Store") return true;
  const profilePlan = (userInfo?.plan || userInfo?.subscriptionType || "").toString();
  return (
    userInfo?.showCase === true ||
    userInfo?.showCase === "true" ||
    userInfo?.showcase === true ||
    userInfo?.showcase === "true" ||
    /startseller|seller/i.test(profilePlan)
  );
};

export function useProductForm({ productType, productId, userInfo, plan, currencyCode = "EUR" }) {
  const history = useHistory();
  const { showErrorToast, showSuccessToast } = useToastContext();
  const productService = useMemo(() => new ProductService(), []);
  const { uploadFile } = useUploadFiles();
  const { userExtrabonus } = useGetUserExtrabonus({ onError: () => {}, typeUser: plan });
  const token = localStorage.getItem("__extraconomy_token");
  const username = localStorage.getItem("__extraconomy_username");
  const downloadableId = parseInt(process.env.REACT_APP_DOWNLOADABLE_ID, 10);
  const cashbackBounds = useMemo(() => getCashbackBoundsByPlan(plan), [plan]);
  const canPublishProducts = useMemo(
    () => canStorePublishProducts(plan, userInfo),
    [plan, userInfo],
  );
  const isEdit = !!productId;
  const isPhysicalProduct = productType === 1;
  const isDownloadableProduct = productType === 3;
  const isStockManagedProduct = !isDownloadableProduct;

  const [loading, setLoading] = useState(!!productId);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [productState, setProductState] = useState(isPhysicalProduct ? "NEW" : null);
  const [priceUnique, setPriceUnique] = useState(null);
  const [discountPercent, setDiscountPercent] = useState(null);
  const [percentExtraCoin, setPercentExtraCoin] = useState(cashbackBounds.min);
  const [images, setImages] = useState([]);
  const [stock, setStock] = useState(0);
  const [height, setHeight] = useState(null);
  const [width, setWidth] = useState(null);
  const [length, setLength] = useState(null);
  const [weight, setWeight] = useState(null);
  const [notifyAllUsers, setNotifyAllUsers] = useState(false);
  const [notifyMyEconomicCircle, setNotifyMyEconomicCircle] = useState(false);
  const [freightOption, setFreightOption] = useState(0);
  const [downloadableFile, setDownloadableFile] = useState(null);
  const [downloadableSample, setDownloadableSample] = useState(null);
  const [downloadFileType, setDownloadFileType] = useState(null);
  const [publishNow, setPublishNow] = useState(false);
  const [hasVariations, setHasVariations] = useState(false);
  const [variationAttributes, setVariationAttributes] = useState([]);
  const [variationCombinations, setVariationCombinations] = useState([]);
  const [originalAttributeIds, setOriginalAttributeIds] = useState([]);
  const [originalVariationIds, setOriginalVariationIds] = useState([]);
  const [characteristicGroups, setCharacteristicGroups] = useState([]);
  const [originalTechnicalInfo, setOriginalTechnicalInfo] = useState([]);
  const [rawProduct, setRawProduct] = useState(null);
  // Banggood foi descontinuado: o estoque desses produtos não pode mais ser alterado
  const isStockLocked = Boolean(rawProduct?.product?.productIdBanggood);
  const [showCostCalculator, setShowCostCalculator] = useState(false);

  const hasToCalculateFreight =
    isPhysicalProduct &&
    selectedCategory &&
    selectedCategory.id !== downloadableId &&
    selectedSubcategory &&
    !selectedSubcategory?.service;

  const notHaveFreeSendCloud = userInfo?.country !== "IT";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await productService.getCategories();
        if (cancelled) return;
        const raw = getCategoriesList(res);
        setCategories(raw.map((cat) => ({ ...cat, _label: getCategoryLabel(cat) })));
      } catch {
        if (!cancelled) showErrorToast({ detail: "unexpectedErrorRegisterProduct" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productService, showErrorToast]);

  useEffect(() => {
    if (!selectedCategory) {
      setSubcategories([]);
      return;
    }
    setSubcategories(
      (selectedCategory.subCategories ?? []).map((s) => ({ ...s, _label: getCategoryLabel(s) })),
    );
  }, [selectedCategory]);

  const loadEditData = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const [productRes, techRes, categoriesRes] = await Promise.all([
        productService.getProductById(productId),
        productService.getTechnicalInfo(productId),
        productService.getCategories(),
      ]);
      if (productRes?.messageCode !== 200001 || !productRes?.result) {
        showErrorToast({ detail: "errorLoadingProduct" });
        return;
      }
      const wrapper = productRes.result;
      const p = wrapper.product;
      setRawProduct(wrapper);
      const cats = getCategoriesList(categoriesRes).map((cat) => ({
        ...cat,
        _label: getCategoryLabel(cat),
      }));
      setCategories(cats);
      setTitle(p.title || "");
      setDescription(p.description || "");
      setPriceUnique(p.priceUnique ?? null);
      setDiscountPercent(promotionalPriceToDiscount(p.priceUnique, p.promotionalPriceUnique));
      setPercentExtraCoin(p.percentExtraCoin ?? p.percentExtracoin ?? cashbackBounds.min);
      setProductState(p.productState || p.stateofproduct || (isPhysicalProduct ? "NEW" : null));
      setHeight(p.height ?? null);
      setWidth(p.width ?? null);
      setLength(p.length ?? null);
      setWeight(p.weight ?? null);
      setFreightOption(mapShippingOptionToFreight(p.shippingOption));
      setStock(p.amountInStock ?? 0);
      setPublishNow(p.status === "PUBLISHED" || p.productStatus === "PUBLISHED");
      setDownloadFileType((p.downloadtype || p.downloadType || "").toUpperCase() || null);
      if (p.urldownload) {
        setDownloadableFile({ url: p.urldownload, fileName: p.urldownload, previewUrl: p.urldownload });
      }
      if (p.urldownloadsample) {
        setDownloadableSample({
          url: p.urldownloadsample,
          fileName: p.urldownloadsample,
          previewUrl: p.urldownloadsample,
        });
      }
      const imgs = [];
      for (let i = 1; i <= 10; i += 1) {
        const id = p[`imageid${i}`];
        if (id) {
          imgs.push({
            id,
            fileName: `image-${i}`,
            previewUrl: cloudinaryPreview(id),
          });
        }
      }
      setImages(imgs);
      const cat = cats.find((c) => (c.subCategories || []).some((s) => s.id === p.subCategory?.id));
      if (cat) {
        setSelectedCategory(cat);
        const subs = (cat.subCategories || []).map((s) => ({ ...s, _label: getCategoryLabel(s) }));
        setSubcategories(subs);
        setSelectedSubcategory(subs.find((s) => s.id === p.subCategory?.id) || null);
      }

      const tech = techRes?.messageCode === 200001 && Array.isArray(techRes.result) ? techRes.result : [];
      setOriginalTechnicalInfo(tech);
      if (tech.length) {
        setCharacteristicGroups([
          {
            localId: createLocalId(),
            name: "",
            items: tech.map((item) => ({
              localId: createLocalId(),
              id: item.id,
              key: item.key || "",
              value: item.value || "",
            })),
          },
        ]);
      }

      const attrs = wrapper.attributes || [];
      const variations = wrapper.productVariations || [];
      if (attrs.length || variations.length) {
        setHasVariations(true);
        const loadedAttrs = [];
        for (const attr of attrs) {
          const valuesRes = await productService.getAttributeValues(token, attr.id, username);
          const values =
            valuesRes?.messageCode === 200001 && Array.isArray(valuesRes.result)
              ? valuesRes.result.map((v) => ({
                  localId: String(v.id),
                  id: v.id,
                  value: v.attributeValue || v.value || "",
                }))
              : [];
          loadedAttrs.push({
            localId: String(attr.id),
            id: attr.id,
            name: attr.attribute,
            values,
          });
        }
        setVariationAttributes(loadedAttrs);
        setOriginalAttributeIds(attrs.map((a) => a.id));
        setOriginalVariationIds(variations.map((v) => v.id));
        setVariationCombinations(
          variations.map((variation) => {
            const parts = [variation.attribute1, variation.attribute2].filter(Boolean);
            const label = parts.map((part) => part.attributeValue).join(" / ");
            return {
              key: label,
              label,
              parts: parts.map((part) => ({ value: part.attributeValue })),
              price: variation.price ?? variation.priceUnique ?? null,
              stock: variation.amountInStock ?? 0,
              imageId: variation.imageid || null,
              previewUrl: variation.imageid ? cloudinaryPreview(variation.imageid) : null,
              imageFile: null,
              variationId: variation.id,
            };
          }),
        );
      }
    } catch {
      showErrorToast({ detail: "errorLoadingProduct" });
    } finally {
      setLoading(false);
    }
  }, [
    productId,
    productService,
    showErrorToast,
    cashbackBounds.min,
    isPhysicalProduct,
    token,
    username,
  ]);

  useEffect(() => {
    loadEditData();
  }, [loadEditData]);

  const updateVariationAttributes = useCallback((next) => {
    setVariationAttributes(next);
    setVariationCombinations((prev) => mergeCombinations(prev, buildVariationCombinations(next)));
  }, []);

  const addImages = useCallback((files) => {
    if (!files?.length) return;
    setImages((prev) => [...(prev || []), ...files].slice(0, MAX_IMAGES));
  }, []);

  const removeImage = useCallback((index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const saveCharacteristicGroup = useCallback((group) => {
    const nextGroup = {
      localId: createLocalId(),
      name: (group?.name || "").trim(),
      items: (group?.items || []).map((item) => ({
        localId: createLocalId(),
        id: item.id || null,
        key: item.key,
        value: item.value,
      })),
    };
    setCharacteristicGroups((prev) => [...prev, nextGroup]);
    return nextGroup;
  }, []);

  const removeCharacteristicGroup = useCallback((index) => {
    setCharacteristicGroups((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const uploadImage = useCallback(
    async (file) => {
      const blob = file instanceof Blob ? file : file.file instanceof Blob ? file.file : null;
      if (!blob) return file.id || null;
      const res = await productService.postUploadImage(token, blob);
      if (res?.messageCode !== 200000) {
        showErrorToast(res?.messageCode ? { code: res.messageCode } : { detail: "unexpectedErrorUploadImg" });
        return null;
      }
      return res?.result ?? null;
    },
    [productService, token, showErrorToast],
  );

  const syncTechnicalInfo = useCallback(
    async (targetProductId) => {
      const items = characteristicGroups.flatMap((group) =>
        (group.items || []).filter((item) => item.key?.trim() && item.value?.trim()),
      );
      const keptIds = new Set(items.filter((item) => item.id).map((item) => item.id));
      for (const original of originalTechnicalInfo) {
        if (!keptIds.has(original.id)) {
          await productService.deleteTechnicalInfo(token, original.id, username);
        }
      }
      for (const item of items) {
        if (item.id) {
          const original = originalTechnicalInfo.find((info) => info.id === item.id);
          if (original && (original.key !== item.key || original.value !== item.value)) {
            await productService.postEditTechnicalInfo(token, item.id, item.key.trim(), item.value.trim(), username);
          }
        } else {
          await productService.postRegisterTechnicalInfo(
            token,
            targetProductId,
            item.key.trim(),
            item.value.trim(),
            username,
          );
        }
      }
    },
    [characteristicGroups, originalTechnicalInfo, productService, token, username],
  );

  const syncVariations = useCallback(
    async (targetProductId, fallbackPrice) => {
      if (!hasVariations || !isPhysicalProduct) return true;
      const attrs = variationAttributes.filter((attr) => attr.name?.trim() && attr.values?.length);
      if (!attrs.length) return true;

      const allHaveIds =
        originalVariationIds.length > 0 &&
        variationCombinations.every((item) => item.variationId) &&
        attrs.every((attr) => attr.id);

      if (allHaveIds) {
        for (const combo of variationCombinations) {
          const price = combo.price || fallbackPrice;
          if (price) {
            await productService.postSetPriceByVariation(token, combo.variationId, price, username);
          }
          if (!isStockLocked) {
            await productService.postSetStockByVariation(
              token,
              combo.variationId,
              targetProductId,
              combo.stock ?? 0,
              username,
            );
          }
          let imageId = combo.imageId || null;
          if (combo.imageFile) imageId = await uploadImage(combo.imageFile);
          if (imageId) {
            await productService.postSetImageByVariation(token, combo.variationId, imageId, username);
          }
        }
        return true;
      }

      for (const variationId of originalVariationIds) {
        await productService.deleteVariation(token, variationId, username);
      }
      for (const attributeId of originalAttributeIds) {
        await productService.postUnassignAttribute(attributeId, targetProductId);
      }

      for (const attr of attrs) {
        const created = await productService.postAttributeRegister(token, attr.name.trim(), username);
        let attributeId = created?.result?.id || created?.result?.attribute?.id;
        if (!attributeId) {
          const listRes = await productService.getAttributes(token, username);
          const found = (listRes?.result || []).find((item) => item.attribute === attr.name.trim());
          attributeId = found?.id;
        }
        if (!attributeId || (created?.messageCode && created.messageCode !== 200000)) {
          showErrorToast({ code: created?.messageCode });
          return false;
        }
        for (const value of attr.values.filter((v) => v.value?.trim())) {
          const valueRes = await productService.postRegisterAttributeValue(
            token,
            attributeId,
            value.value.trim(),
            username,
          );
          if (valueRes?.messageCode !== 200000) {
            showErrorToast({ code: valueRes?.messageCode });
            return false;
          }
        }
        const assignRes = await productService.postAssignAttribute(
          token,
          attributeId,
          targetProductId,
          username,
        );
        if (assignRes?.messageCode !== 200000) {
          showErrorToast({ code: assignRes?.messageCode });
          return false;
        }
      }

      const generated = await productService.postCreateVariations(token, targetProductId, username);
      if (generated?.messageCode !== 200000 && generated?.messageCode !== 200001) {
        showErrorToast({ code: generated?.messageCode });
        return false;
      }

      const refreshed = await productService.getProductById(targetProductId);
      const createdVariations = refreshed?.result?.productVariations || [];
      for (const createdVariation of createdVariations) {
        const label = [createdVariation.attribute1, createdVariation.attribute2]
          .filter(Boolean)
          .map((part) => part.attributeValue)
          .join(" / ");
        const local = variationCombinations.find((item) => item.key === label);
        const price = local?.price || fallbackPrice;
        if (price) {
          await productService.postSetPriceByVariation(token, createdVariation.id, price, username);
        }
        if (!isStockLocked) {
          await productService.postSetStockByVariation(
            token,
            createdVariation.id,
            targetProductId,
            local?.stock ?? 0,
            username,
          );
        }
        let imageId = local?.imageId || null;
        if (local?.imageFile) imageId = await uploadImage(local.imageFile);
        if (imageId) {
          await productService.postSetImageByVariation(token, createdVariation.id, imageId, username);
        }
      }
      return true;
    },
    [
      hasVariations,
      isPhysicalProduct,
      variationAttributes,
      originalVariationIds,
      originalAttributeIds,
      variationCombinations,
      isStockLocked,
      productService,
      token,
      username,
      showErrorToast,
      uploadImage,
    ],
  );

  const handleSubmit = useCallback(async () => {
    const subCategoryId = isDownloadableProduct
      ? getSubcategoryIdForDownloadType(downloadFileType)
      : selectedSubcategory?.id;
    const firstVariationPrice = variationCombinations.find((item) => item.price > 0)?.price;
    const productPrice = hasVariations ? firstVariationPrice : priceUnique;
    const promotionalPrice = discountToPromotionalPrice(productPrice, discountPercent);

    if (!title?.trim() || !subCategoryId) {
      showErrorToast({ detail: "pleaseFillOutAll" });
      return;
    }
    if (hasVariations) {
      const missingVariationPrice =
        !(variationCombinations || []).length ||
        variationCombinations.some((item) => !(item.price > 0));
      if (missingVariationPrice) {
        showErrorToast({ detail: "pleaseFillOutAll" });
        return;
      }
    } else if (!priceUnique || priceUnique <= 0) {
      showErrorToast({ detail: "pleaseFillOutAll" });
      return;
    }
    const descriptionText = (description || "").replace(/<[^>]*>/g, "").trim();
    if (!descriptionText) {
      showErrorToast({ detail: "pleaseFillOutAll" });
      return;
    }
    if (isPhysicalProduct && !productState) {
      showErrorToast({ detail: "pleaseFillOutAll" });
      return;
    }
    if (!images?.length) {
      showErrorToast({ detail: "pleaseUploadAtLeastOne" });
      return;
    }
    if (isDownloadableProduct && !downloadableFile) {
      showErrorToast({ detail: "pleaseFillOutAll" });
      return;
    }
    if (
      hasToCalculateFreight &&
      (!(height > 0) || !(width > 0) || !(length > 0) || !(weight > 0) || freightOption < 1)
    ) {
      showErrorToast({ detail: freightOption < 1 ? "pleaseFillOutAll" : "dimensionsGreaterThanZero" });
      return;
    }
    if (
      percentExtraCoin != null &&
      (percentExtraCoin < cashbackBounds.min || percentExtraCoin > cashbackBounds.max)
    ) {
      showErrorToast({ code: getCashbackErrorCodeByPlan(plan) });
      return;
    }
    if (notifyAllUsers && (userExtrabonus?.balance ?? 0) < 50) {
      showErrorToast({ detail: "notifyInsufficientBalance" });
      return;
    }
    if (notifyMyEconomicCircle && (userExtrabonus?.balance ?? 0) < 20) {
      showErrorToast({ detail: "notifyInsufficientBalance" });
      return;
    }

    setSaving(true);
    try {
      const imageIds = [];
      for (let i = 0; i < Math.min(images.length, MAX_IMAGES); i += 1) {
        const id = await uploadImage(images[i]);
        if (id) imageIds[i] = id;
        else if (images[i]?.id) imageIds[i] = images[i].id;
      }
      if (!imageIds.filter(Boolean).length) {
        setSaving(false);
        return;
      }

      let urldownload = downloadableFile?.url || rawProduct?.product?.urldownload || null;
      let urldownloadsample = downloadableSample?.url || rawProduct?.product?.urldownloadsample || null;
      if (isDownloadableProduct && downloadableFile && (downloadableFile instanceof Blob || downloadableFile.file)) {
        const blob = downloadableFile instanceof Blob ? downloadableFile : downloadableFile.file;
        const urlMain = await uploadFile(blob);
        if (!urlMain) {
          setSaving(false);
          return;
        }
        urldownload = urlMain;
      }
      if (isDownloadableProduct && downloadableSample && (downloadableSample instanceof Blob || downloadableSample.file)) {
        const blob = downloadableSample instanceof Blob ? downloadableSample : downloadableSample.file;
        const urlSample = await uploadFile(blob);
        if (urlSample) urldownloadsample = urlSample;
      }

      const productStatus = publishNow && canPublishProducts ? "PUBLISHED" : "UNPUBLISHED";
      let targetId = productId;

      if (!isEdit) {
        const productObj = {
          title: title.trim(),
          description: description || "",
          model: null,
          imageid1: imageIds[0] ?? null,
          imageid2: imageIds[1] ?? null,
          imageid3: imageIds[2] ?? null,
          imageid4: imageIds[3] ?? null,
          imageid5: imageIds[4] ?? null,
          imageid6: imageIds[5] ?? null,
          imageid7: imageIds[6] ?? null,
          imageid8: imageIds[7] ?? null,
          imageid9: imageIds[8] ?? null,
          imageid10: imageIds[9] ?? null,
          percentExtraCoin: percentExtraCoin ?? 0,
          priceUnique: Number(productPrice),
          promotionalPriceUnique: promotionalPrice,
          productState: isPhysicalProduct ? productState || "NEW" : null,
          productStatus,
          subCategory: { id: subCategoryId },
          user: { username },
          brand: null,
          type: null,
          genderProduct: null,
          conditionsUsedProduct: null,
          sendBy: "SELLER",
          shippingOption: isPhysicalProduct ? mapFreightToShippingOption(freightOption) : null,
          shippingPrice: 0,
          downloadable: isDownloadableProduct,
          downloadtype: isDownloadableProduct ? downloadFileType : null,
          urldownload,
          urldownloadsample,
          cdnType: isDownloadableProduct ? "CLOUDINARY" : null,
          downloadType: isDownloadableProduct ? downloadFileType : null,
          service: productType === 2,
          height: hasToCalculateFreight ? Number(height) || 0 : 0,
          width: hasToCalculateFreight ? Number(width) || 0 : 0,
          length: hasToCalculateFreight ? Number(length) || 0 : 0,
          weight: hasToCalculateFreight ? Number(weight) || 0 : 0,
        };
        const res = await productService.postRegisterProduct(token, {
          ...productObj,
          notifyAllUsers,
          notifyMyEconomicCircle,
        });
        if (res?.messageCode !== 200000) {
          showErrorToast({ code: res.messageCode, message: res.message });
          setSaving(false);
          return;
        }
        targetId = res?.result?.id;
      } else {
        const p = rawProduct.product;
        const resEdit = await productService.postEditProduct(
          token,
          username,
          title.trim(),
          description || "",
          percentExtraCoin,
          imageIds[0] ?? null,
          imageIds[1] ?? null,
          imageIds[2] ?? null,
          imageIds[3] ?? null,
          imageIds[4] ?? null,
          p.id,
          hasToCalculateFreight ? Number(weight) || 0 : 0,
          hasToCalculateFreight ? Number(width) || 0 : 0,
          hasToCalculateFreight ? Number(height) || 0 : 0,
          hasToCalculateFreight ? Number(length) || 0 : 0,
          subCategoryId,
          null,
          null,
          null,
          null,
          productType === 2,
          isDownloadableProduct,
          isDownloadableProduct ? downloadFileType : null,
          isDownloadableProduct ? "CLOUDINARY" : null,
          isDownloadableProduct ? urldownload : null,
          isDownloadableProduct ? urldownloadsample : null,
          isPhysicalProduct ? mapFreightToShippingOption(freightOption) : null,
          null,
          isPhysicalProduct ? productState : null,
          0,
          imageIds[5] ?? null,
          imageIds[6] ?? null,
          imageIds[7] ?? null,
          imageIds[8] ?? null,
          imageIds[9] ?? null,
          notifyAllUsers,
          notifyMyEconomicCircle,
        );
        if (resEdit?.messageCode !== 200000) {
          showErrorToast({ code: resEdit?.messageCode });
          setSaving(false);
          return;
        }
        if (!hasVariations) {
          const resPrice = await productService.postSetPriceByProduct(
            token,
            username,
            Number(productPrice),
            promotionalPrice,
            p.id,
          );
          if (resPrice?.messageCode !== 200000) {
            showErrorToast({ code: resPrice?.messageCode });
            setSaving(false);
            return;
          }
        }
        if ((p.status || p.productStatus) !== productStatus) {
          await productService.postModifyStatus(token, p.id, productStatus, username);
        }
      }

      if (targetId && isStockManagedProduct && !hasVariations && !isStockLocked) {
        await productService.postSetStockByProduct(token, targetId, stock ?? 0, username);
      }
      if (targetId) await syncTechnicalInfo(targetId);
      if (targetId && hasVariations) {
        const ok = await syncVariations(targetId, productPrice);
        if (!ok) {
          setSaving(false);
          return;
        }
      }

      showSuccessToast({ detail: isEdit ? "productUpdatedSuccessfully" : "productRegisteredSuccessfully" });
      history.push("/dashboard-store/products");
    } catch {
      showErrorToast({ detail: "unexpectedErrorRegisterProduct" });
    } finally {
      setSaving(false);
    }
  }, [
    isDownloadableProduct,
    downloadFileType,
    selectedSubcategory,
    priceUnique,
    discountPercent,
    variationCombinations,
    hasVariations,
    title,
    isPhysicalProduct,
    productState,
    images,
    downloadableFile,
    hasToCalculateFreight,
    height,
    width,
    length,
    weight,
    freightOption,
    percentExtraCoin,
    cashbackBounds,
    plan,
    notifyAllUsers,
    notifyMyEconomicCircle,
    userExtrabonus,
    canPublishProducts,
    publishNow,
    productId,
    isEdit,
    description,
    username,
    productType,
    downloadableSample,
    rawProduct,
    isStockManagedProduct,
    isStockLocked,
    stock,
    uploadImage,
    uploadFile,
    productService,
    token,
    syncTechnicalInfo,
    syncVariations,
    showErrorToast,
    showSuccessToast,
    history,
  ]);

  return {
    loading,
    saving,
    isEdit,
    productType,
    currencyCode,
    cashbackBounds,
    canPublishProducts,
    categories,
    subcategories,
    selectedCategory,
    setSelectedCategory,
    selectedSubcategory,
    setSelectedSubcategory,
    title,
    setTitle,
    description,
    setDescription,
    productState,
    setProductState,
    priceUnique,
    setPriceUnique,
    discountPercent,
    setDiscountPercent,
    percentExtraCoin,
    setPercentExtraCoin,
    images,
    setImages,
    addImages,
    removeImage,
    stock,
    setStock,
    isStockLocked,
    height,
    setHeight,
    width,
    setWidth,
    length,
    setLength,
    weight,
    setWeight,
    notifyAllUsers,
    setNotifyAllUsers,
    notifyMyEconomicCircle,
    setNotifyMyEconomicCircle,
    freightOption,
    setFreightOption,
    downloadableFile,
    setDownloadableFile,
    downloadableSample,
    setDownloadableSample,
    downloadFileType,
    setDownloadFileType,
    publishNow,
    setPublishNow,
    hasVariations,
    setHasVariations,
    variationAttributes,
    setVariationAttributes: updateVariationAttributes,
    variationCombinations,
    setVariationCombinations,
    characteristicGroups,
    saveCharacteristicGroup,
    removeCharacteristicGroup,
    hasToCalculateFreight,
    notHaveFreeSendCloud,
    showCostCalculator,
    openCostCalculator: () => setShowCostCalculator(true),
    closeCostCalculator: () => setShowCostCalculator(false),
    handleSubmit,
  };
}
